import React from 'react';
import { Check } from 'lucide-react';
import { pricingPlans, type CheckoutPlanId, type PricingPlan, type PricingPlanId } from '../../data/pricingPlans';
interface PlanOptionCardsProps {
  /** Frontend checkout action — no payment logic lives in this component. */
  onCheckout: (plan: CheckoutPlanId) => void;
  /** Plan the user is already on, rendered as "Current plan". */
  currentPlanId?: PricingPlanId;
  /** Checkout currently in flight. */
  pendingPlan?: CheckoutPlanId | null;
  /** Which plans to render. Defaults to the full five-tier system. */
  plans?: PricingPlan[];
  /** Free-plan CTA. Omit to hide the free tier's action entirely. */
  onStartFree?: () => void;
  /** Free CTA label, e.g. "Start free scan". */
  freeCtaLabel?: string;
}
const FRAME: Record<PricingPlan['accent'], string> = {
  neutral: 'border-gray-800 hover:border-gray-600',
  red: 'border-[#FF0000]/60 hover:border-[#FF0000]',
  gold: 'border-[#FFD700]/50 hover:border-[#FFD700]'
};
const BADGE: Record<PricingPlan['accent'], string> = {
  neutral: 'bg-gray-800 text-gray-300',
  red: 'bg-[#FF0000] text-white',
  gold: 'bg-[#FFD700] text-black'
};
const CTA: Record<PricingPlan['accent'], string> = {
  neutral: 'border border-gray-700 bg-transparent text-white hover:border-gray-500 hover:bg-white/5',
  red: 'bg-[#FF0000] text-white hover:bg-[#D90000]',
  gold: 'bg-[#FFD700] text-black hover:bg-[#F5C400]'
};
/**
 * The compact plan strip used on authenticated surfaces (onboarding preview,
 * upload upgrade panel). Deliberately terse — label, price, one line of
 * entitlement, one action. The full feature comparison lives in the plans modal.
 */
export function PlanOptionCards({
  onCheckout,
  currentPlanId,
  pendingPlan = null,
  plans = pricingPlans,
  onStartFree,
  freeCtaLabel = 'Start free scan'
}: PlanOptionCardsProps) {
  return <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-777" data-mp="1oad737q1v72x">
      {plans.map(plan => {
      // A single scan is a one-time purchase, so it is always re-buyable —
      // it never renders as the locked-in "current plan".
      const isCurrent = plan.id === currentPlanId && plan.id !== 'single_scan';
      const isFree = plan.id === 'free';
      const isLoading = pendingPlan === plan.id;
      return <li key={plan.id} className={`flex flex-col border bg-[#0a0a0a] p-4 transition-colors duration-200 ease-out ${FRAME[plan.accent]}`} data-id="element-778" data-mp="6axee7nbyz5k">
            <div className="mb-3 flex h-5 items-start" data-id="element-779" data-mp="3sinajr258s24">
              {plan.badge && <span className={`px-2 py-0.5 font-mono text-[9px] font-black uppercase tracking-[0.15em] ${BADGE[plan.accent]}`} data-id="element-780" data-mp="3skhtiztuua3k">
                  {plan.badge}
                </span>}
            </div>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-781" data-mp="34udt11byqqgs">
              {plan.label}
            </p>
            <p className="mt-2 text-xl font-black leading-none tracking-tight text-white" data-id="element-782" data-mp="39u05v9nvq4p2">
              {plan.price}
              <span className="ml-1 font-mono text-[11px] font-bold uppercase tracking-wide text-gray-500" data-id="element-783" data-mp="2m5204u5e6kb3">
                {plan.id === 'free' ? '' : plan.id === 'single_scan' ? '/ scan' : plan.id === 'weekly' ? '/ week' : plan.id === 'monthly' ? '/ month' : '/ year'}
              </span>
            </p>
            <p className="mt-2 flex-1 font-mono text-[11px] leading-relaxed text-gray-400" data-id="element-784" data-mp="351tqton9k1p6">
              {plan.summary}
            </p>
            {plan.id === 'yearly' && plan.savings && <p className="mt-2 font-mono text-[10px] uppercase tracking-wide text-[#FFD700]" data-id="element-785" data-mp="3dy1aezuxpkd4">
                {plan.savings} vs monthly
              </p>}
            <div className="mt-4" data-id="element-786" data-mp="3nvfedzzo8goj">
              {isCurrent ? <p className="flex items-center justify-center gap-1.5 border border-gray-800 bg-gray-900/60 py-2.5 font-mono text-[10px] font-black uppercase tracking-[0.15em] text-gray-400" data-id="element-787" data-mp="1l80ghnfgepsq">
                  <Check size={12} strokeWidth={3} aria-hidden="true" data-id="element-788" data-mp="1uxjv2gw4vxhs" />
                  Current plan
                </p> : isFree ? onStartFree ? <button type="button" onClick={onStartFree} className={`w-full py-2.5 text-[11px] font-black uppercase tracking-wider transition-colors duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505] ${CTA.neutral}`} data-id="element-789" data-mp="128mi8taoyswf">
                    {freeCtaLabel}
                  </button> : <p className="py-2.5 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-gray-600" data-id="element-790" data-mp="2nlfsjntj1tfm">
                    Included at signup
                  </p> : <button type="button" onClick={() => onCheckout(plan.id as CheckoutPlanId)} disabled={pendingPlan !== null} aria-busy={isLoading} className={`w-full py-2.5 text-[11px] font-black uppercase tracking-wider transition-colors duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505] ${CTA[plan.accent]}`} data-id="element-791" data-mp="gwvw8djkmcgp">
                  {isLoading ? 'Processing…' : plan.cta}
                </button>}
            </div>
          </li>;
    })}
    </ul>;
}